import { DragEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { AssignedPerson } from 'models/OperationRequest';

import { initials, personName, shortName } from '../data/assignment';

interface PersonAvatarProps {
  person: Pick<AssignedPerson, 'leadId' | 'firstName' | 'lastName'>;
  size?: number;
}

const hueOf = (id: string) =>
  [...id].reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 360, 7);

export function PersonAvatar({ person, size = 28 }: PersonAvatarProps) {
  const hue = hueOf(person.leadId);
  return (
    <span
      className="assignment-board__avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: `hsl(${hue}, 45%, 85%)`,
        color: `hsl(${hue}, 45%, 30%)`,
      }}
    >
      {initials(person)}
    </span>
  );
}

interface AssignedPersonChipProps {
  person: AssignedPerson;
  compact?: boolean;
  onDragStart: (e: DragEvent<HTMLSpanElement>) => void;
  onDragEnd: () => void;
  onOpen: (leadId: string) => void;
  onRemove: () => void;
}

function AssignedPersonChip({
  person,
  compact,
  onDragStart,
  onDragEnd,
  onOpen,
  onRemove,
}: AssignedPersonChipProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.assign_people.board';

  return (
    <span
      className="assignment-board__chip"
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        onDragStart(e);
      }}
      onDragEnd={onDragEnd}
      onClick={(e) => e.stopPropagation()}
    >
      <PersonAvatar person={person} size={compact ? 18 : 22} />
      <span
        className="assignment-board__chip-name"
        title={personName(person)}
        onClick={() => onOpen(person.leadId)}
      >
        {compact ? shortName(person) : personName(person)}
      </span>
      <button
        type="button"
        className="assignment-board__chip-remove"
        title={t(`${i18nBase}.remove`)}
        aria-label={t(`${i18nBase}.remove`)}
        onClick={onRemove}
      >
        ✕
      </button>
    </span>
  );
}

export default AssignedPersonChip;
